import { effectiveZoom } from './effective-zoom';

/**
 * ECharts hit-testing under the density zoom (GEN-E-100-05).
 *
 * zrender takes a mouse/pointer event's position inside the chart from `event.offsetX/Y`, which it
 * treats as the chart's own (local) px. Under a CSS `zoom` Chrome reports `offsetX/Y` in screen px,
 * so every hover, tooltip and click landed at local x zoom — at compact a pointer on bar 3 read as
 * 85% of the way there and hit bar 2 or nothing. zrender's other paths (touch, and the document-level
 * listeners it uses while dragging) measure against `getBoundingClientRect()` and are already right.
 *
 * One capturing listener on the document rewrites `offsetX/Y` to local px before zrender reads them,
 * for events inside an ECharts root only, and only when that root is actually zoomed. The value is
 * worked out from `clientX/Y` and the target's rect rather than by dividing the browser's `offsetX`, so
 * it stays right if a browser starts reporting `offsetX` in local px.
 */
const CHART_ROOT_SELECTOR = '[_echarts_instance_]';

const POINTER_EVENTS = [
  'pointerdown', 'pointermove', 'pointerup', 'pointerover', 'pointerout', 'pointercancel',
  'mousedown', 'mousemove', 'mouseup', 'mouseover', 'mouseout',
  'click', 'dblclick', 'contextmenu', 'wheel', 'mousewheel'
];

const installedOn = new WeakSet<Document>();

/** The event's position inside `target`'s padding box, in `target`'s local px. */
export function localPointerOffset(
  event: Pick<MouseEvent, 'clientX' | 'clientY'>,
  target: Element,
  zoom: number
): { x: number; y: number } {
  const rect = target.getBoundingClientRect();
  return {
    x: (event.clientX - rect.left) / zoom - target.clientLeft, // density-pointer-allow: the zoom helper itself, divides by zoom
    y: (event.clientY - rect.top) / zoom - target.clientTop // density-pointer-allow: the zoom helper itself, divides by zoom
  };
}

/** Rewrites `offsetX/Y` on an event that targets a zoomed ECharts chart. Leaves every other event alone. */
export function correctChartPointerOffset(event: MouseEvent): void {
  const target = event.target;
  if (!(target instanceof Element) || !target.closest(CHART_ROOT_SELECTOR)) {
    return;
  }
  const zoom = effectiveZoom(target);
  if (zoom === 1 || !(zoom > 0)) {
    return;
  }
  const offset = localPointerOffset(event, target, zoom);
  Object.defineProperty(event, 'offsetX', { configurable: true, value: offset.x });
  Object.defineProperty(event, 'offsetY', { configurable: true, value: offset.y });
}

/**
 * Installs the correction once per document. Call it outside the Angular zone: it listens to every
 * pointer move on the page and must not schedule change detection.
 */
export function installChartPointerZoomFix(doc: Document): void {
  if (!doc || installedOn.has(doc)) {
    return;
  }
  installedOn.add(doc);
  const listener = (event: Event) => correctChartPointerOffset(event as MouseEvent);
  for (const type of POINTER_EVENTS) {
    doc.addEventListener(type, listener, { capture: true, passive: true });
  }
}
